import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Popper,
  ClickAwayListener,
  Tooltip,
  Menu,
  CircularProgress,
  ListItemText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "../../features/auth/authSlice";
import {
  KeyboardArrowDown as ArrowDownIcon,
  NoteAdd as ComposeIcon,
  // ViewSidebar as SidebarIcon,
  // ChevronRight as ChevronRightIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  InsertDriveFile as FileIcon,
  Code as CodeIcon,
  Description as DocIcon,
  Language as WebIcon,
  Storage as AllIcon,
  Add as AddIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
} from "@mui/icons-material";
// import { useChat } from '../../hooks/useChat';
import { chatService } from "../../services/chatService";
import { fileService } from "../../services/fileService";
import { ChatThread, ChatMessage, SlashCommand, SLASH_COMMANDS } from "../../types/chat";
import NotionStyleMessageList, {
  ExtendedChatMessage,
} from "./NotionStyleMessageList";
import ChatFileSearchPanel from "./ChatFileSearchPanel";
import type { SearchResult } from "./SearchResultsCard";
import { getDocumentDisplayParts } from "../../utils/documentUtils";
import { buildSourceDocumentUrl } from "../../utils/sourceNavigation";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  selectCurrentMode,
  selectCurrentModeData,
  setCurrentMode,
  setShowModeSelector,
  switchMode,
} from "@features/chat/chatModeSlice";
import ModeIndicator from "./ModeIndicator";
import ModeSelector from "./ModeSelector";
import SlashCommandSuggestions from "./SlashCommandSuggestions";
import { useTranslation } from "react-i18next";
import { sortThreadsForHistory } from "./chatHistory";

export type { SearchResult } from "./SearchResultsCard";

export const buildDocumentSourceUrl = (
  result: SearchResult,
  query: string,
): string => {
  return buildSourceDocumentUrl(result, query);
};

export interface AttachedFile {
  id: string;
  name: string;
  path: string;
  type: "file" | "document" | "code";
  title?: string;
  description?: string;
}

export type SourceType = "all" | "web";

interface NotionStyleChatPanelProps {
  open: boolean;
  onClose: () => void;
  width?: number;
  isOverlay?: boolean;
}

const NotionStyleChatPanel: React.FC<NotionStyleChatPanelProps> = ({
  open,
  onClose,
  width = 374,
  isOverlay = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [sourceType, setSourceType] = useState<SourceType>("all");
  const [showFileSuggestions, setShowFileSuggestions] = useState(false);
  const [suggestionAnchor, setSuggestionAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [selectedChat, setSelectedChat] = useState(() => t("chat.newChat"));
  const [isComposing, setIsComposing] = useState(false);
  // Mode system state (Issue #302)
  const [modeSelectorAnchor, setModeSelectorAnchor] =
    useState<HTMLElement | null>(null);
  const [showSlashSuggestions, setShowSlashSuggestions] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Ref to track attached files synchronously (fixes timing issue #122)
  const attachedFilesRef = useRef<AttachedFile[]>([]);
  // Ref to track latest selected thread ID for race condition prevention
  const latestThreadIdRef = useRef<string | null>(null);

  // Real API state
  const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentThread, setCurrentThread] = useState<ChatThread | null>(null);
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [isThreadsLoading, setIsThreadsLoading] = useState(false);
  const [historyMenuAnchor, setHistoryMenuAnchor] = useState<null | HTMLElement>(null);
  const [pinningThreadId, setPinningThreadId] = useState<string | null>(null);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Mode system selectors (Issue #302)
  const currentMode = useAppSelector(selectCurrentMode);
  const currentModeData = useAppSelector(selectCurrentModeData);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Debug attached files changes
  // Keep ref in sync with state for synchronous access (fixes timing issue #122)
  useEffect(() => {
    console.log("attachedFiles changed:", attachedFiles);
    attachedFilesRef.current = attachedFiles;
  }, [attachedFiles]);

  // Convert ChatMessage[] to ExtendedChatMessage[]
  const convertToExtendedMessages = useCallback(
    (threadMessages: ChatMessage[]): ExtendedChatMessage[] =>
      threadMessages.map((msg) => ({
        ...msg,
        searchResults: msg.searchResults
          ? {
              query: msg.searchResults.query,
              results: msg.searchResults.results.map((r) => ({
                id: r.id,
                title: r.title,
                path: [r.fileName || "Unknown"],
                type: "document" as const,
                snippet: r.snippet,
                relevance: r.relevanceScore,
                relevanceLabel: r.relevanceLabel,
                rawRelevance: r.rawRelevanceScore,
                relevanceSource: r.relevanceSource,
              })),
            }
          : undefined,
      })),
    [],
  );

  // Load existing threads when authenticated - don't auto-create
  useEffect(() => {
    const loadThreads = async () => {
      if (open && isAuthenticated) {
        try {
          // Fetch existing threads - this GET request will set the CSRF cookie
          const fetchedThreads = await chatService.getThreads();
          const sortedThreads = sortThreadsForHistory(fetchedThreads);
          setThreads(sortedThreads);
          if (sortedThreads.length > 0) {
            setCurrentThread(sortedThreads[0]);
            setSelectedChat(sortedThreads[0].title || t("chat.chatFallback"));
            setAttachedFiles([]);
            attachedFilesRef.current = [];
            // Load messages for the first thread
            const threadMessages = await chatService.getMessages(sortedThreads[0].id);
            setMessages(convertToExtendedMessages(threadMessages));
          }
          // Don't auto-create a thread - let user click "New Chat" button
        } catch (error) {
          console.error("Failed to load threads:", error);
        }
      }
    };

    loadThreads();
    // Load threads on open/auth only. t() just supplies a fallback chat title;
    // making it a dependency would reload threads and reset the user's selected
    // thread whenever the language changes (PR #790 review).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated, convertToExtendedMessages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Check for slash command (Issue #302)
    if (newValue.startsWith("/")) {
      setShowSlashSuggestions(true);
      setShowFileSuggestions(false);
    } else {
      setShowSlashSuggestions(false);
    }

    // Check for @ mention - open file search panel
    const lastChar = newValue[newValue.length - 1];
    const lastTwoChars = newValue.slice(-2);

    if (lastChar === "@" || (lastTwoChars[0] === " " && lastChar === "@")) {
      setShowFileSuggestions(true);
      setShowSlashSuggestions(false);
      setSuggestionAnchor(e.currentTarget);
    } else if (showFileSuggestions) {
      const atIndex = newValue.lastIndexOf("@");
      if (atIndex === -1) {
        setShowFileSuggestions(false);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !isComposing) {
      event.preventDefault();
      // Don't send if file suggestions are open
      if (showFileSuggestions) {
        return;
      }
      // For slash commands, only block if there are actually matching commands
      // to prevent blocking users from sending messages like "/xyz"
      if (showSlashSuggestions && inputValue.startsWith("/")) {
        const query = inputValue.toLowerCase().slice(1);
        const hasMatchingCommands = SLASH_COMMANDS.some(
          (cmd) =>
            query === "" ||
            cmd.command.slice(1).startsWith(query) ||
            cmd.label.toLowerCase().includes(query) ||
            cmd.modeId.includes(query),
        );
        if (hasMatchingCommands) {
          return; // Block Enter when there are matching suggestions
        }
      }
      handleSendMessage();
    }
    if (event.key === "Escape") {
      if (showFileSuggestions) {
        setShowFileSuggestions(false);
      }
      if (showSlashSuggestions) {
        setShowSlashSuggestions(false);
      }
    }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  // Slash command selection handler (Issue #302)
  const handleSlashCommandSelect = useCallback(
    (command: SlashCommand) => {
      // Special handling for DRBFM - navigate to dedicated page
      if (command.modeId === "drbfm") {
        setInputValue("");
        setShowSlashSuggestions(false);
        navigate("/drbfm");
        return;
      }

      // Switch mode - use async switchMode for persistence if thread exists
      if (currentThread?.id) {
        dispatch(
          switchMode({
            threadId: currentThread.id,
            request: { mode_id: command.modeId },
          }),
        );
      } else {
        dispatch(setCurrentMode(command.modeId));
      }
      // Clear input and close suggestions
      setInputValue("");
      setShowSlashSuggestions(false);
      // Add system message about mode switch
      const systemMsg: ExtendedChatMessage = {
        id: `system-mode-${Date.now()}`,
        role: "system" as const,
        content: t("chat.modeSwitched", {
          mode: t(`chat.modes.${command.modeId}.name`, command.label),
          description: t(`chat.modes.${command.modeId}.description`, command.description),
        }),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, systemMsg]);
      // Focus back to input
      inputRef.current?.focus();
    },
    [dispatch, currentThread?.id, navigate, t],
  );

  // Close slash suggestions handler
  const handleCloseSlashSuggestions = useCallback(() => {
    setShowSlashSuggestions(false);
  }, []);

  // Check if message is asking for file summary or analysis
  const isFileSummaryRequest = (message: string): boolean => {
    const summaryKeywords = [
      "要約",
      "要約して",
      "まとめ",
      "まとめて",
      "サマリー",
      "summary",
      "summarize",
    ];
    const analysisKeywords = [
      "詳細",
      "詳しく",
      "深く",
      "分析",
      "技術的",
      "deep",
      "detail",
      "technical",
      "analyze",
    ];
    const allKeywords = [...summaryKeywords, ...analysisKeywords];
    const lowerMessage = message.toLowerCase();

    // Check both original message and lowercased version for better matching
    return allKeywords.some(
      (keyword) =>
        message.includes(keyword) ||
        lowerMessage.includes(keyword.toLowerCase()),
    );
  };

  // Check if message is asking for deep analysis
  const isDeepAnalysisRequest = (message: string): boolean => {
    // Deep analysis keywords - explicit requests for detailed/deep analysis
    const deepKeywords = [
      "詳細",
      "詳しく",
      "深く",
      "分析",
      "技術的",
      "deep",
      "detail",
      "technical",
      "analyze",
    ];
    const lowerMessage = message.toLowerCase();

    // Check both original message and lowercased version for better matching
    return deepKeywords.some(
      (keyword) =>
        message.includes(keyword) ||
        lowerMessage.includes(keyword.toLowerCase()),
    );
  };

  const handleFileSummarization = async (
    userMessage: string,
    assistantId: string,
  ) => {
    try {
      // Use ref for immediate access to latest attached files (fixes timing issue #122)
      const currentAttachedFiles = attachedFilesRef.current;
      if (currentAttachedFiles.length === 0) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId
              ? {
                  ...msg,
                  content: t("chat.noFilesSelectedForSummary"),
                }
              : msg,
          ),
        );
        return;
      }

      const fileIds = currentAttachedFiles.map((f) => f.id);
      const useDeepAnalysis = isDeepAnalysisRequest(userMessage);

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: useDeepAnalysis
                  ? t("chat.deepAnalysisInProgress")
                  : t("chat.fileAnalysisInProgress"),
              }
            : msg,
        ),
      );

      // Get file summaries with deep analysis if requested
      const summaryResponse = await fileService.getMultipleFileSummaries(
        fileIds,
        true,
        true,
        useDeepAnalysis,
      );

      // Build the response message
      let responseContent = "";

      if (fileIds.length === 1) {
        // Single file summary
        const fileId = fileIds[0];
        const file = attachedFiles.find((f) => f.id === fileId);
        const summary =
          summaryResponse.individualSummaries?.[fileId] ||
          t("chat.summaryUnavailable");
        const summaryType = useDeepAnalysis
          ? t("chat.deepTechnicalAnalysis")
          : t("chat.summary");

        responseContent = `${t("chat.singleFileSummaryHeading", {
          title: file?.title || file?.name,
          type: summaryType,
        })}\n\n${summary}`;
      } else {
        // Multiple file summaries
        const summaryType = useDeepAnalysis
          ? t("chat.deepTechnicalAnalysis")
          : t("chat.summary");
        responseContent = `${t("chat.multiFileSummaryHeading", {
          count: fileIds.length,
          type: summaryType,
        })}\n\n`;

        // Individual summaries
        if (summaryResponse.individualSummaries) {
          responseContent += `${t(useDeepAnalysis
            ? "chat.individualFileAnalysesHeading"
            : "chat.individualFileSummariesHeading")}\n\n`;
          attachedFiles.forEach((file, index) => {
            const summary =
              summaryResponse.individualSummaries?.[file.id] ||
              t("chat.summaryUnavailable");
            responseContent += `**${index + 1}. ${file.title || file.name}**\n${summary}\n\n`;
          });
        }

        // Combined summary - different header based on analysis type
        if (summaryResponse.combinedSummary) {
          responseContent += useDeepAnalysis
            ? `${t("chat.combinedTechnicalAnalysisHeading")}\n\n`
            : `${t("chat.combinedTechnicalInsightsHeading")}\n\n`;
          responseContent += summaryResponse.combinedSummary;
        }
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId ? { ...msg, content: responseContent } : msg,
        ),
      );
    } catch (error) {
      console.error("Failed to get file summaries:", error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: t("chat.summaryError"),
              }
            : msg,
        ),
      );
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();

    // Auto-create a thread if none exists
    let activeThread = currentThread;
    if (!activeThread) {
      try {
        activeThread = await chatService.createThread(t("chat.newChat"));
        setCurrentThread(activeThread);
        setSelectedChat(activeThread.title || t("chat.newChat"));
        setThreads((prev) => sortThreadsForHistory([activeThread!, ...prev]));
      } catch (error) {
        console.error("Failed to create thread:", error);
        return;
      }
    }
    setInputValue("");
    setIsLoading(true);

    // Add context indicator if files are selected (only if not already shown for same files)
    const currentFiles = attachedFilesRef.current;
    if (currentFiles.length > 0 && !isFileSummaryRequest(userMessage)) {
      const fileNames = currentFiles.map((f) => f.title || f.name).join(", ");
      const contextContent = t("chat.askingAboutFiles", {
        count: currentFiles.length,
        fileNames,
      });
      setMessages((prev) => {
        const lastContextMsg = [...prev].reverse().find((m) => m.role === "system" && m.content.startsWith("📎"));
        if (lastContextMsg && lastContextMsg.content === contextContent) {
          return prev;
        }
        const contextMsg: ExtendedChatMessage = {
          id: `context-${Date.now()}`,
          role: "system" as const,
          content: contextContent,
          timestamp: new Date(),
        };
        return [...prev, contextMsg];
      });
    }

    // Add user message immediately
    const userMsg: ExtendedChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Initialize assistant message for streaming
    const assistantId = Date.now().toString() + "-assistant";
    const assistantMsg: ExtendedChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, assistantMsg]);

    try {
      // Check if this is a file summary request
      if (isFileSummaryRequest(userMessage)) {
        await handleFileSummarization(userMessage, assistantId);
        setIsLoading(false);
        // Keep attached files for potential follow-up questions
        return;
      }

      let fullMessage = "";

      // Build selectedFiles from attachedFiles for deep-dive context
      const selectedFileIds = attachedFilesRef.current.map((f) => f.id);

      // Stream the message with mode context (Issue #302) and selected files
      await chatService.streamMessage(
        activeThread.id,
        userMessage,
        (content) => {
          fullMessage = content;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId ? { ...msg, content: fullMessage } : msg,
            ),
          );
        },
        (results) => {
          // Map API SearchResult to SearchResultsCard format
          // Use getDocumentDisplayParts to avoid double display when title equals fileName
          const mappedResults = results.map((r) => {
            const displayParts = getDocumentDisplayParts(r.title, r.fileName);
            return {
              id: r.id,
              title: displayParts.main || "Unknown",
              // Only show path (fileName) if it's different from title
              path: displayParts.subtitle ? [displayParts.subtitle] : [],
              type: "document" as const,
              snippet: r.snippet || "",
              excerpt: r.excerpt || r.snippet || "",
              relevance: r.relevanceScore || 0.5,
              relevanceLabel: r.relevanceLabel,
              rawRelevance: r.rawRelevanceScore,
              relevanceSource: r.relevanceSource,
              citationIndex: r.citationIndex,
              selected: r.selected,
              pageNumber: r.pageNumber,
              segmentId: r.segmentId,
            };
          });

          // Update message with search results
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    searchResults:
                      results.length > 0
                        ? {
                            query: userMessage,
                            results: mappedResults,
                          }
                        : undefined,
                  }
                : msg,
            ),
          );
        },
        (modeInfo) => {
          // On complete - handle mode info from response (Issue #302)
          setIsLoading(false);
          if (modeInfo?.modeSwitched && modeInfo.newMode) {
            dispatch(setCurrentMode(modeInfo.newMode));
          }
          // Store grounding status + verification/web-search summaries on the assistant message
          if (
            modeInfo?.groundingStatus ||
            modeInfo?.verificationSummary ||
            modeInfo?.webSearchStatus
          ) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? {
                      ...msg,
                      ...(modeInfo.groundingStatus && { groundingStatus: modeInfo.groundingStatus }),
                      ...(modeInfo.verificationSummary && { verificationSummary: modeInfo.verificationSummary }),
                      ...(modeInfo.webSearchStatus && { webSearchStatus: modeInfo.webSearchStatus }),
                    }
                  : msg,
              ),
            );
          }
        },
        {
          mode: currentMode,
          modeData: currentModeData,
          includeWebSearch: sourceType === 'web',
          selectedFiles:
            selectedFileIds.length > 0 ? selectedFileIds : undefined,
          onTitleUpdate: (title: string) => {
            setSelectedChat((prevChat) => {
              if (currentThread?.id === activeThread.id) {
                return title;
              }
              return prevChat;
            });
            setCurrentThread((prev) => {
              if (prev && prev.id === activeThread.id) {
                return { ...prev, title };
              }
              return prev;
            });
            setThreads((prev) =>
              sortThreadsForHistory(prev.map((t) =>
                t.id === activeThread.id ? { ...t, title } : t,
              )),
            );
          },
        },
      );

      // Attached files persist until user explicitly removes them
    } catch (error) {
      console.error("Failed to send message:", error);
      // Remove the empty assistant message on error
      setMessages((prev) => prev.filter((msg) => msg.id !== assistantId));
    } finally {
      // Safety net: ``isLoading`` is also cleared inside the
      // ``onComplete`` callback when the SSE ``completed`` event
      // arrives. If the stream ends without that event — backend
      // crash before completion emit, network drop mid-stream,
      // proxy timeout — the older code left the 「もう少し...」
      // spinner up forever. Clearing here in ``finally`` guarantees
      // the UI exits the loading state regardless of how the
      // request terminates.
      setIsLoading(false);
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleAddContext = () => {
    // Show file suggestions dropdown
    setShowFileSuggestions(true);
    setSuggestionAnchor(inputRef.current);
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case "code":
        return <CodeIcon sx={{ fontSize: 16 }} />;
      case "document":
        return <DocIcon sx={{ fontSize: 16 }} />;
      default:
        return <FileIcon sx={{ fontSize: 16 }} />;
    }
  };

  const createNewThread = async () => {
    try {
      setIsLoading(true);
      const thread = await chatService.createThread();
      setCurrentThread(thread);
      setMessages([]);
      setAttachedFiles([]);
      attachedFilesRef.current = [];
      setSelectedChat(thread.title || t("chat.newChat"));
      setThreads((prev) => sortThreadsForHistory([thread, ...prev]));
    } catch (error) {
      console.error("Failed to create thread:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    createNewThread();
    setSelectedChat(t("chat.newThread"));
  };

  const handleHistoryMenuOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setHistoryMenuAnchor(event.currentTarget);
    setIsThreadsLoading(true);
    try {
      const latestThreads = await chatService.getThreads();
      setThreads(sortThreadsForHistory(latestThreads));
    } catch (error) {
      console.error("Failed to refresh threads:", error);
    } finally {
      setIsThreadsLoading(false);
    }
  };

  const handleHistoryMenuClose = () => {
    setHistoryMenuAnchor(null);
  };

  const handleThreadSelect = async (thread: ChatThread) => {
    handleHistoryMenuClose();
    if (thread.id === currentThread?.id) return;

    const selectedThreadId = thread.id;
    latestThreadIdRef.current = selectedThreadId;

    setCurrentThread(thread);
    setSelectedChat(thread.title || t("chat.newChat"));
    setAttachedFiles([]);
    attachedFilesRef.current = [];
    setMessages([]);
    setIsLoading(true);

    try {
      const threadMessages = await chatService.getMessages(selectedThreadId);
      if (latestThreadIdRef.current === selectedThreadId) {
        setMessages(convertToExtendedMessages(threadMessages));
      }
    } catch (error) {
      console.error("Failed to load thread messages:", error);
    } finally {
      if (latestThreadIdRef.current === selectedThreadId) {
        setIsLoading(false);
      }
    }
  };

  const handleThreadPinToggle = async (
    event: React.MouseEvent<HTMLButtonElement>,
    thread: ChatThread,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const nextPinned = !thread.isPinned;
    const optimisticThread: ChatThread = {
      ...thread,
      isPinned: nextPinned,
      pinnedAt: nextPinned ? new Date() : null,
    };

    setPinningThreadId(thread.id);
    setThreads((prev) =>
      sortThreadsForHistory(prev.map((item) =>
        item.id === thread.id ? optimisticThread : item,
      )),
    );
    setCurrentThread((prev) =>
      prev?.id === thread.id ? { ...prev, ...optimisticThread } : prev,
    );

    try {
      const updatedThread = await chatService.updateThreadPin(thread.id, nextPinned);
      setThreads((prev) =>
        sortThreadsForHistory(prev.map((item) =>
          item.id === thread.id ? { ...item, ...updatedThread } : item,
        )),
      );
      setCurrentThread((prev) =>
        prev?.id === thread.id ? { ...prev, ...updatedThread } : prev,
      );
    } catch (error) {
      console.error("Failed to update thread pin:", error);
      setThreads((prev) =>
        sortThreadsForHistory(prev.map((item) =>
          item.id === thread.id ? thread : item,
        )),
      );
      setCurrentThread((prev) =>
        prev?.id === thread.id ? { ...prev, ...thread } : prev,
      );
    } finally {
      setPinningThreadId(null);
    }
  };

  const sortedHistoryThreads = sortThreadsForHistory(threads);
  const pinnedHistoryThreads = sortedHistoryThreads.filter((thread) => thread.isPinned);
  const recentHistoryThreads = sortedHistoryThreads.filter((thread) => !thread.isPinned);
  const dateLocale = i18n.language?.startsWith("ja") ? "ja-JP" : "en-US";

  const renderThreadMenuItems = (items: ChatThread[]) =>
    items.map((thread) => {
      const isCurrentThread = thread.id === currentThread?.id;
      const title = thread.title || t("chat.newChat");
      return (
        <MenuItem
          key={thread.id}
          onClick={() => handleThreadSelect(thread)}
          selected={isCurrentThread}
          sx={{
            py: 1,
            px: 2,
            gap: 1,
            backgroundColor: thread.isPinned ? "rgba(245, 158, 11, 0.06)" : undefined,
            "&.Mui-selected": {
              backgroundColor: "rgba(55, 53, 47, 0.06)",
              "&:hover": {
                backgroundColor: "rgba(55, 53, 47, 0.1)",
              },
            },
            "&:hover": {
              backgroundColor: thread.isPinned
                ? "rgba(245, 158, 11, 0.1)"
                : "rgba(55, 53, 47, 0.04)",
            },
          }}
        >
          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: isCurrentThread ? 500 : 400,
                }}
              >
                {title}
              </Typography>
            }
            secondary={
              <Typography variant="caption" color="text.secondary">
                {new Date(thread.updatedAt).toLocaleDateString(dateLocale, {
                  month: "numeric",
                  day: "numeric",
                })}
              </Typography>
            }
          />
          <Tooltip title={thread.isPinned ? t("chat.unpinThread") : t("chat.pinThread")}>
            <span>
              <IconButton
                aria-label={t(thread.isPinned ? "chat.unpinThreadAria" : "chat.pinThreadAria", { title })}
                size="small"
                disabled={pinningThreadId === thread.id}
                onClick={(event) => handleThreadPinToggle(event, thread)}
                sx={{
                  color: thread.isPinned ? "#b7791f" : "text.disabled",
                  "&:hover": {
                    color: "#b7791f",
                    backgroundColor: "rgba(245, 158, 11, 0.12)",
                  },
                }}
              >
                {thread.isPinned ? (
                  <PinIcon fontSize="inherit" />
                ) : (
                  <PinOutlinedIcon fontSize="inherit" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </MenuItem>
      );
    });

  if (!open) return null;

  return (
    <>
      {/* Backdrop for overlay mode */}
      {isOverlay && (
        <Box
          onClick={onClose}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1199,
            backgroundColor: "rgba(0,0,0,0.3)",
          }}
        />
      )}
      <Paper
        elevation={isOverlay ? 8 : 0}
        sx={{
          position: "fixed",
          right: 0,
          top: 64,
          bottom: 0,
          width: width,
          display: "flex",
          flexDirection: "column",
          zIndex: 1200,
          borderLeft: "1px solid",
          borderColor: "divider",
          backgroundColor: "#FFFFFF",
        }}
      >
      {/* Header */}
      <Box
        sx={{
          height: 46,
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Chat History Dropdown */}
        <Button
          onClick={handleHistoryMenuOpen}
          startIcon={null}
          endIcon={<ArrowDownIcon sx={{ fontSize: 16 }} />}
          sx={{
            flex: 1,
            justifyContent: "space-between",
            color: "rgb(50, 48, 44)",
            textTransform: "none",
            fontSize: 14,
            fontWeight: 400,
            px: 1,
            py: 0.5,
            minHeight: 28,
            "&:hover": {
              backgroundColor: "rgba(55, 53, 47, 0.08)",
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "200px",
            }}
          >
            {selectedChat}
          </Typography>
        </Button>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 0.25 }}>
          <IconButton
            size="small"
            onClick={handleNewChat}
            sx={{
              width: 28,
              height: 28,
              color: "rgb(50, 48, 44)",
              "&:hover": {
                backgroundColor: "rgba(55, 53, 47, 0.08)",
              },
            }}
          >
            <ComposeIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {/* <IconButton
            size="small"
            sx={{
              width: 28,
              height: 28,
              color: 'rgb(50, 48, 44)',
              '&:hover': {
                backgroundColor: 'rgba(55, 53, 47, 0.08)',
              },
            }}
          >
            <SidebarIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 28,
              height: 28,
              color: 'rgb(50, 48, 44)',
              '&:hover': {
                backgroundColor: 'rgba(55, 53, 47, 0.08)',
              },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </IconButton> */}
        </Box>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 2,
          py: 2,
          backgroundColor: "#FFFFFF",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
            },
          },
        }}
      >
        {/* Date/AI Label */}
        <Box
          sx={{
            textAlign: "center",
            py: 2,
            color: "rgba(70, 68, 64, 0.45)",
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          {new Date().toLocaleDateString(dateLocale, {
            weekday: "long",
            month: "short",
            day: "numeric",
          })}{" "}
          ・ AIONA AI
        </Box>

        <NotionStyleMessageList
          messages={messages as any}
          isLoading={isLoading}
          onSearchResultClick={(result, query) => {
            navigate(buildDocumentSourceUrl(result, query));
          }}
          onAddToContext={async (results: SearchResult[]) => {
            console.log("NotionStyleChatPanel onAddToContext called:", results);

            // Single file processing for immediate feedback
            for (const result of results) {
              try {
                // Check if already attached
                if (attachedFiles.some((f) => f.id === result.id)) {
                  console.log("File already in context:", result.id);
                  continue;
                }

                // Fetch the file data from the list to get complete FileInfo
                const allFiles = await fileService.getFilesForSelection();
                const fileInfo = allFiles.find((f) => f.id === result.id);

                let attachedFile: AttachedFile;
                if (fileInfo) {
                  // Convert FileInfo to AttachedFile
                  attachedFile = {
                    id: fileInfo.id,
                    name: fileInfo.name,
                    path: fileInfo.path,
                    type: fileInfo.type as "file" | "document" | "code",
                    title: fileInfo.title,
                    description: fileInfo.description,
                  };
                } else {
                  // Fallback: create AttachedFile from search result
                  attachedFile = {
                    id: result.id,
                    name: result.title,
                    path: result.path.join("/"),
                    type: result.type as "file" | "document" | "code",
                    title: result.title,
                    description: result.snippet,
                  };
                }

                // Add single file to attached files immediately
                console.log("Adding file to attachedFiles:", attachedFile);
                // Update ref synchronously for immediate availability (fixes timing issue #122)
                attachedFilesRef.current = [
                  ...attachedFilesRef.current,
                  attachedFile,
                ];
                setAttachedFiles((prev) => {
                  const updatedFiles = [...prev, attachedFile];
                  console.log("Updated attachedFiles:", updatedFiles);
                  return updatedFiles;
                });

                // Show confirmation message for single file
                setMessages((prev) => [
                  ...prev,
                  {
                    id: `system-${Date.now()}-${result.id}`,
                    role: "system" as const,
                    content: t("chat.fileAddedToContext", {
                      name: attachedFile.title || attachedFile.name,
                    }),
                    timestamp: new Date(),
                  } as ExtendedChatMessage,
                ]);
              } catch (error) {
                console.error("Error adding file to context:", error);
              }
            }
          }}
          selectedFileIds={attachedFiles.map((f) => f.id)}
          onRetryWithMode={(message) => {
            setInputValue(message);
            dispatch(setShowModeSelector(true));
          }}
        />
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area - RightFixedChatPanel Style */}
      <Box
        sx={{
          flexShrink: 0,
          minHeight: 120,
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        {/* Top Section - File Attachments */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {/* Add Context Button */}
            <Chip
              icon={<AddIcon sx={{ fontSize: 18 }} />}
              label={t('chat.addContext')}
              size="small"
              onClick={handleAddContext}
              variant="outlined"
              sx={{
                height: 28,
                borderRadius: "14px",
                borderColor: "action.disabled",
                "&:hover": {
                  backgroundColor: "action.hover",
                  borderColor: "action.active",
                },
              }}
            />

            {/* Attached Files */}
            {attachedFiles.map((file) => (
              <Chip
                key={file.id}
                icon={getFileIcon(file.type)}
                label={file.name}
                size="small"
                onDelete={() => handleRemoveFile(file.id)}
                sx={{
                  height: 28,
                  borderRadius: "14px",
                  backgroundColor: "action.selected",
                  "& .MuiChip-icon": {
                    color: "primary.main",
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Middle Section - Input Field */}
        <Box sx={{ mb: 1 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            placeholder={t('chat.inputPlaceholder')}
            disabled={isLoading}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.default",
                fontSize: "14px",
                "& fieldset": {
                  borderColor: "divider",
                },
                "&:hover fieldset": {
                  borderColor: "action.hover",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
              },
              "& .MuiInputBase-input": {
                padding: "12px",
                minHeight: "24px",
              },
            }}
          />
        </Box>

        {/* Bottom Section - Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          {/* Left Side - Model and Source Selection */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Attach File Button */}
            <Tooltip title={t('chat.attachFile')}>
              <IconButton
                size="small"
                onClick={handleAddContext}
                disabled={isLoading}
                sx={{ color: "action.active" }}
              >
                <AttachIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Source Selection */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={sourceType}
                onChange={(e: SelectChangeEvent<SourceType>) =>
                  setSourceType(e.target.value as SourceType)
                }
                displayEmpty
                sx={{
                  height: 28,
                  fontSize: 14,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "transparent",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "action.hover",
                  },
                  "& .MuiSelect-select": {
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                <MenuItem value="all">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AllIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2">{t('chat.allSources')}</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="web">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <WebIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2">{t('chat.webOnly')}</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Mode Indicator (Issue #302) */}
            <Box ref={(el: HTMLElement | null) => setModeSelectorAnchor(el)}>
              <ModeIndicator />
            </Box>
          </Box>

          {/* Right Side - Send Button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Send Button */}
            <Tooltip title={t('chat.sendMessage')}>
              <span>
                <IconButton
                  onClick={handleSendMessage}
                  disabled={isLoading || !inputValue.trim()}
                  size="small"
                  sx={{
                    backgroundColor: inputValue.trim()
                      ? "primary.main"
                      : "action.disabled",
                    color: "background.paper",
                    "&:hover": {
                      backgroundColor: inputValue.trim()
                        ? "primary.dark"
                        : "action.disabled",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "action.disabled",
                      color: "text.disabled",
                    },
                    width: 28,
                    height: 28,
                  }}
                >
                  <SendIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Enhanced File Search Panel */}
      <Popper
        open={showFileSuggestions}
        anchorEl={suggestionAnchor}
        placement="top-start"
        style={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={() => setShowFileSuggestions(false)}>
          <Box>
            <ChatFileSearchPanel
              selectedFileIds={attachedFiles.map((f) => f.id)}
              onSelectFiles={(selectedFiles, allPanelFileIds) => {
                // IDs of all files the panel knows about (selected or not)
                const panelManagedIds = new Set(allPanelFileIds);

                // Convert panel selections to AttachedFile format
                const panelAttachedFiles: AttachedFile[] = selectedFiles.map(
                  (file) => ({
                    id: file.id,
                    name: file.name,
                    path: file.path,
                    type: file.type as "file" | "document" | "code",
                    title: file.title,
                    description: file.description,
                  }),
                );

                // Keep files from other sources (e.g., SearchResultsCard) that the panel doesn't manage
                const externalFiles = attachedFiles.filter(
                  (af) => !panelManagedIds.has(af.id),
                );

                // Replace placeholder files with real metadata from externalFiles if available
                const externalFilesMap = new Map(
                  externalFiles.map((f) => [f.id, f]),
                );
                const panelFilesWithRealMetadata = panelAttachedFiles.map(
                  (f) => {
                    // If this file has placeholder data ('Unknown') and we have real metadata, use the real data
                    if (f.name === "Unknown" && externalFilesMap.has(f.id)) {
                      return externalFilesMap.get(f.id)!;
                    }
                    return f;
                  },
                );

                // Filter out files from externalFiles that were merged into panelFilesWithRealMetadata
                const panelFileIds = new Set(
                  panelFilesWithRealMetadata.map((f) => f.id),
                );
                const remainingExternalFiles = externalFiles.filter(
                  (f) => !panelFileIds.has(f.id),
                );

                // Replace the entire state (idempotent)
                const merged = [
                  ...remainingExternalFiles,
                  ...panelFilesWithRealMetadata,
                ];
                attachedFilesRef.current = merged;
                setAttachedFiles(merged);
              }}
            />
          </Box>
        </ClickAwayListener>
      </Popper>

      {/* Chat History Menu */}
      <Menu
        anchorEl={historyMenuAnchor}
        open={Boolean(historyMenuAnchor)}
        onClose={handleHistoryMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 280,
              maxHeight: 400,
              borderRadius: 2,
              boxShadow: 8,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            {t("chat.history")}
          </Typography>
        </Box>
        {isThreadsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : threads.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t("chat.noHistory")}
            </Typography>
          </MenuItem>
        ) : (
          [
            ...(pinnedHistoryThreads.length > 0 ? [
              <Box key="pinned-heading" sx={{ px: 2, pt: 1, pb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#b7791f", fontWeight: 700, letterSpacing: 0.2 }}
                >
                  {t("chat.pinned")}
                </Typography>
              </Box>,
            ] : []),
            ...renderThreadMenuItems(pinnedHistoryThreads),
            ...(recentHistoryThreads.length > 0 ? [
              <Box key="recent-heading" sx={{ px: 2, pt: 1, pb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.2 }}
                >
                  {t("chat.recentChats")}
                </Typography>
              </Box>,
            ] : []),
            ...renderThreadMenuItems(recentHistoryThreads),
          ]
        )}
      </Menu>

      {/* Slash Command Suggestions (Issue #302) */}
      <SlashCommandSuggestions
        inputValue={inputValue}
        anchorEl={inputRef.current}
        onSelectCommand={handleSlashCommandSelect}
        onClose={handleCloseSlashSuggestions}
        isVisible={showSlashSuggestions}
      />

      {/* Mode Selector Popover (Issue #302) */}
      <ModeSelector
        anchorEl={modeSelectorAnchor}
        threadId={currentThread?.id}
      />
    </Paper>
    </>
  );
};

export default NotionStyleChatPanel;
